import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, Box, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, Chip,
  TextField, MenuItem, CircularProgress, Alert, IconButton,
  Tooltip
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import api from '../utils/api';

const levelColors = {
  INFO: 'info',
  ERROR: 'error',
  WARNING: 'warning',
};

const statusColors = {
  pending: 'default',
  running: 'info',
  completed: 'success',
  failed: 'error',
  cancelled: 'warning',
};

const LogsPage = () => {
  const [entries, setEntries] = useState([]);
  const [totalRuns, setTotalRuns] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterLevel, setFilterLevel] = useState('');
  const [filterPipeline, setFilterPipeline] = useState('');
  const [pipelines, setPipelines] = useState([]);

  const fetchPipelines = useCallback(async () => {
    try {
      const res = await api.get('/pipelines');
      setPipelines(res.data || []);
    } catch {
      // ignore
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        skip: page * rowsPerPage,
        limit: rowsPerPage,
      });
      if (filterLevel) params.set('level', filterLevel);
      if (filterPipeline) params.set('pipeline_id', filterPipeline);

      const res = await api.get(`/runs/logs/all?${params}`);
      setEntries(res.data.entries || []);
      setTotalRuns(res.data.total_runs || 0);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load logs');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filterLevel, filterPipeline]);

  useEffect(() => {
    fetchPipelines();
  }, [fetchPipelines]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Execution Logs</Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchLogs}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <TextField
          select
          label="Level"
          value={filterLevel}
          onChange={(e) => { setFilterLevel(e.target.value); setPage(0); }}
          size="small"
          sx={{ minWidth: 130 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="INFO">Info</MenuItem>
          <MenuItem value="ERROR">Error</MenuItem>
          <MenuItem value="WARNING">Warning</MenuItem>
        </TextField>
        <TextField
          select
          label="Pipeline"
          value={filterPipeline}
          onChange={(e) => { setFilterPipeline(e.target.value); setPage(0); }}
          size="small"
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All Pipelines</MenuItem>
          {pipelines.map((p) => (
            <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
          ))}
        </TextField>
      </Box>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Time</TableCell>
              <TableCell>Level</TableCell>
              <TableCell>Pipeline</TableCell>
              <TableCell>Run</TableCell>
              <TableCell>Node</TableCell>
              <TableCell>Message</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : entries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  No log entries found. Run a pipeline to see execution logs.
                </TableCell>
              </TableRow>
            ) : (
              entries.map((entry, idx) => (
                <TableRow key={idx} hover sx={entry.level === 'ERROR' ? { bgcolor: 'error.50' } : {}}>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                    {entry.timestamp ? new Date(entry.timestamp).toLocaleString() : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={entry.level}
                      size="small"
                      color={levelColors[entry.level] || 'default'}
                      variant="outlined"
                      sx={{ fontFamily: 'monospace', fontSize: 11 }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: 13 }}>
                    {entry.pipeline_name || '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`#${entry.run_id}`}
                      size="small"
                      color={statusColors[entry.run_status] || 'default'}
                      variant="outlined"
                      sx={{ fontSize: 11 }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: 13 }}>
                    {entry.node || '-'}
                  </TableCell>
                  <TableCell sx={{
                    fontSize: 12,
                    fontFamily: entry.level === 'ERROR' ? 'monospace' : 'inherit',
                    maxWidth: 400,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    <Tooltip title={entry.message} placement="top-start">
                      <span>{entry.message}</span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalRuns > 0 ? -1 : 0}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>
    </Container>
  );
};

export default LogsPage;
